import React, { createContext, useContext, useState } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';

const DocumentsContext = createContext();

export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocuments must be used within a DocumentsProvider');
  }
  return context;
};

export const DocumentsProvider = ({ children }) => {
  // Initialize documents from localStorage or use default values
  const [documents, setDocuments] = useState(() => {
    const savedDocuments = localStorage.getItem('facit_documents');
    return savedDocuments ? JSON.parse(savedDocuments) : [
      {
        id: 1,
        type: 'invoice',
        number: 'FAC-1001',
        title: 'Facture Développement Site Web',
        client: 'SARL TechnoAlger',
        date: '2024-01-15',
        dueDate: '2024-02-14',
        amount: 250000,
        status: 'paid',
        category: 'FACTURATION',
        items: [
          { id: 1, name: 'Développement Frontend', quantity: 1, price: 120000 },
          { id: 2, name: 'Développement Backend', quantity: 1, price: 130000 }
        ]
      },
      {
        id: 2,
        type: 'quote',
        number: 'DEV-2001',
        title: 'Devis Système de Gestion',
        client: 'EURL InnovCorp',
        date: '2024-01-10',
        validUntil: '2024-02-10',
        amount: 450000,
        status: 'accepted',
        category: 'OFFRES COMMERCIALES',
        items: [
          { id: 1, name: 'Système ERP', quantity: 1, price: 300000 },
          { id: 2, name: 'Module RH', quantity: 1, price: 100000 },
          { id: 3, name: 'Module Comptabilité', quantity: 1, price: 50000 }
        ]
      },
      {
        id: 3,
        type: 'proforma',
        number: 'PRO-3001',
        title: 'Facture Proforma Formation',
        client: 'Université d\'Alger',
        date: '2024-01-12',
        amount: 75000,
        status: 'pending',
        category: 'FACTURATION',
        items: [
          { id: 1, name: 'Formation Développement Web', quantity: 5, price: 15000 }
        ]
      },
      {
        id: 4,
        type: 'delivery',
        number: 'BL-4001',
        title: 'Bon de Livraison Matériel',
        client: 'SARL TechnoAlger',
        date: '2024-01-20',
        status: 'delivered',
        category: 'LOGISTIQUE',
        items: [
          { id: 1, name: 'Ordinateur Portable HP', quantity: 10, price: 85000 },
          { id: 2, name: 'Écran 24"', quantity: 5, price: 25000 }
        ]
      },
      {
        id: 5,
        type: 'purchase_order',
        number: 'BC-5001',
        title: 'Bon de Commande Serveurs',
        client: 'Ministère de l\'Éducation',
        date: '2024-01-18',
        amount: 1200000,
        status: 'confirmed',
        category: 'OFFRES COMMERCIALES',
        items: [
          { id: 1, name: 'Serveur Dell PowerEdge', quantity: 3, price: 400000 }
        ]
      },
      {
        id: 6,
        type: 'shipping',
        number: 'EXP-6001',
        title: 'Bordereau d\'Expédition',
        client: 'EURL Constantine IT',
        date: '2024-01-22',
        destination: 'Constantine',
        status: 'shipped',
        category: 'LOGISTIQUE',
        items: [
          { id: 1, name: 'Switch Cisco', quantity: 2, price: 45000 },
          { id: 2, name: 'Routeur Mikrotik', quantity: 3, price: 18000 }
        ]
      },
      {
        id: 7,
        type: 'quote_request',
        number: 'DDV-7001',
        title: 'Demande de Devis Audit',
        client: 'Banque d\'Algérie',
        date: '2024-01-25',
        amount: 180000,
        status: 'draft',
        category: 'FACTURATION',
        items: [
          { id: 1, name: 'Audit Sécurité', quantity: 1, price: 180000 }
        ]
      }
    ];
  });

  const addDocument = (document) => {
    const newDocument = {
      id: documents.length + 1,
      ...document,
      date: document.date || new Date().toISOString().split('T')[0]
    };
    
    const updatedDocuments = [...documents, newDocument];
    setDocuments(updatedDocuments);
    
    // Save to localStorage for persistence
    localStorage.setItem('facit_documents', JSON.stringify(updatedDocuments));
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.DOCUMENT_CREATED, newDocument);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'documents');
    
    return newDocument;
  };

  const updateDocument = (id, updatedDocument) => {
    const oldDocument = documents.find(doc => doc.id === id);
    const newDocuments = documents.map(doc => 
      doc.id === id ? { ...doc, ...updatedDocument } : doc
    );
    
    setDocuments(newDocuments);
    
    // Save to localStorage for persistence
    localStorage.setItem('facit_documents', JSON.stringify(newDocuments));
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.DOCUMENT_UPDATED, { 
      oldDocument, 
      newDocument: newDocuments.find(doc => doc.id === id) 
    });
    
    // If status changed, emit special event
    if (oldDocument && updatedDocument.status && oldDocument.status !== updatedDocument.status) {
      eventEmitter.emit(EVENT_TYPES.DOCUMENT_STATUS_CHANGED, {
        documentId: id,
        oldStatus: oldDocument.status,
        newStatus: updatedDocument.status
      });
    }
    
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'documents');
  };

  const deleteDocument = (id) => {
    const documentToDelete = documents.find(doc => doc.id === id);
    const newDocuments = documents.filter(doc => doc.id !== id);
    setDocuments(newDocuments);
    
    // Save to localStorage for persistence
    localStorage.setItem('facit_documents', JSON.stringify(newDocuments));
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.DOCUMENT_DELETED, documentToDelete);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'documents');
  };

  const getDocumentById = (id) => {
    return documents.find(doc => doc.id === id);
  };

  const getDocumentsByType = (type) => {
    return documents.filter(doc => doc.type === type);
  };

  const getDocumentsByClient = (clientId) => {
    return documents.filter(doc => doc.clientId === clientId);
  };

  const getDocumentsByStatus = (status) => {
    return documents.filter(doc => doc.status === status);
  };

  const getDocumentsByCategory = (category) => {
    return documents.filter(doc => doc.category === category);
  };

  const getRecentDocuments = (days = 7) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return documents.filter(doc => {
      const docDate = new Date(doc.date);
      return docDate >= cutoffDate;
    });
  };

  const getDocumentsByDateRange = (startDate, endDate) => {
    return documents.filter(doc => {
      const docDate = new Date(doc.date);
      return docDate >= new Date(startDate) && docDate <= new Date(endDate);
    });
  };

  const getTotalAmount = () => {
    return documents.reduce((sum, doc) => sum + (doc.amount || 0), 0);
  };

  return (
    <DocumentsContext.Provider
      value={{
        documents,
        addDocument,
        updateDocument,
        deleteDocument,
        getDocumentById,
        getDocumentsByType,
        getDocumentsByClient,
        getDocumentsByStatus,
        getDocumentsByCategory,
        getRecentDocuments,
        getDocumentsByDateRange,
        getTotalAmount
      }}
    >
      {children}
    </DocumentsContext.Provider>
  );
};

export default DocumentsContext; 